import { useState, MouseEvent } from "react";
import { Box, HStack } from "@chakra-ui/react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import WobbleBookingMock from "./wobble/WobbleBookingMock";
import WobbleAdminMock from "./wobble/WobbleAdminMock";

const MotionBox = motion(Box);

type View = "booking" | "admin";

const TABS: { key: View; label: string }[] = [
  { key: "booking", label: "Foglalási oldal" },
  { key: "admin", label: "Admin felület" },
];

// A collage of two fully coded (no screenshots) recreations of the real
// Wobble product — the booking flow on a phone, the settings/integrations
// screen on a browser — laid out like the one on wobble.hu itself (desktop
// behind, phone in front). Both mockups are actually clickable: the tab
// pair brings either one to the front (and drives the mobile layout, where
// the two can't sit side by side), and each mockup has its own internal
// interaction (booking steps, settings sub-tabs).
const WobbleShowcase = () => {
  const [active, setActive] = useState<View>("booking");

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useTransform(y, [-140, 140], [4, -4]);
  const rotateY = useTransform(x, [-200, 200], [-4, 4]);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - rect.left - rect.width / 2);
    y.set(e.clientY - rect.top - rect.height / 2);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <Box>
      <HStack
        spacing={1}
        bg="wobble.100"
        borderRadius="full"
        p={1}
        w="fit-content"
        mx={{ base: "auto", lg: 0 }}
        mb={{ base: 6, lg: 8 }}
      >
        {TABS.map((tab) => (
          <Box
            key={tab.key}
            as="button"
            type="button"
            onClick={() => setActive(tab.key)}
            px={4}
            py={2}
            borderRadius="full"
            fontSize="sm"
            fontWeight="600"
            bg={active === tab.key ? "wobble.600" : "transparent"}
            color={active === tab.key ? "white" : "wobble.700"}
            transition="all 0.25s ease"
            _hover={active === tab.key ? {} : { bg: "wobble.200" }}
          >
            {tab.label}
          </Box>
        ))}
      </HStack>

      <Box
        position="relative"
        h={{ base: "auto", lg: "540px" }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        sx={{ perspective: "1400px" }}
      >
        {/* Desktop admin mockup */}
        <MotionBox
          position={{ base: "relative", lg: "absolute" }}
          top={{ lg: 0 }}
          right={{ lg: 0 }}
          w={{ base: "100%", lg: "88%" }}
          display={{ base: active === "admin" ? "block" : "none", lg: "block" }}
          zIndex={active === "admin" ? 2 : 1}
          cursor={active === "admin" ? "default" : "pointer"}
          onClick={active === "admin" ? undefined : () => setActive("admin")}
          style={{ rotateX, rotateY }}
          animate={{
            scale: active === "admin" ? 1 : 0.92,
            opacity: active === "admin" ? 1 : 0.45,
          }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <WobbleAdminMock />
        </MotionBox>

        {/* Mobile booking mockup */}
        <MotionBox
          position={{ base: "relative", lg: "absolute" }}
          bottom={{ lg: "-4%" }}
          left={{ lg: "-2%" }}
          w={{ base: "68%", sm: "48%", lg: "30%" }}
          maxW={{ base: "260px", lg: "none" }}
          mx={{ base: "auto", lg: 0 }}
          mt={{ base: 6, lg: 0 }}
          display={{ base: active === "booking" ? "block" : "none", lg: "block" }}
          zIndex={active === "booking" ? 2 : 1}
          cursor={active === "booking" ? "default" : "pointer"}
          onClick={active === "booking" ? undefined : () => setActive("booking")}
          style={{ rotateX, rotateY }}
          animate={{
            scale: active === "booking" ? 1 : 0.88,
            opacity: active === "booking" ? 1 : 0.45,
          }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <WobbleBookingMock />
        </MotionBox>
      </Box>
    </Box>
  );
};

export default WobbleShowcase;
